/**
 * Registry of `client_api_key.category` values owned by connect flows, and
 * how such keys render in the shared key table. `category` is otherwise a
 * free-text admin taxonomy field; entries here carry flow semantics.
 *
 * `reserved: true` means the category's key lifecycle is machine-owned, so
 * the admin create-key path must refuse to hand-mint one. Two reasons a
 * category earns it: a rotation flow bulk-revokes keys with the category (an
 * admin-typed duplicate would be silently revoked by the next run — the
 * managed/candidate categories), or the owning flow expects exactly the keys
 * it minted itself (slack-agent, which never rotates).
 */

import { ECS_KEY_CATEGORY, RUNPOD_KEY_CATEGORY, SLACK_AGENT_KEY_CATEGORY } from "@packages/llm-gateway/key-categories";

/**
 * LEGACY — no longer minted (AIR-1390). Keys the retired browser "Connect
 * Codex" wizard created and wrote into ~/.codex/config.toml as an
 * `experimental_bearer_token`. The CLI (`aireserve configure`, cli-managed
 * keys + short-lived session tokens) is the managed Codex path now. Existing
 * codex-managed keys keep serving until their owner revokes them, so the
 * category stays registered: the key table still renders the badge, the
 * proxy still attributes their traffic to Codex, and the reserved flag still
 * stops an admin from hand-minting a look-alike.
 */
export const CODEX_MANAGED_KEY_CATEGORY = "codex-managed";
/**
 * LEGACY — no longer minted (AIR-1390). The two-phase-rotation candidate of
 * the retired wizard; any row still carrying it was abandoned mid-run and is
 * safe to revoke.
 */
export const CODEX_MANAGED_CANDIDATE_CATEGORY = "codex-managed-candidate";
/** Display name the retired Connect Codex wizard gave its keys; kept so the key list reads them. */
export const CODEX_MANAGED_KEY_NAME = "CODEX-MANAGED-KEY";

export const CLAUDE_CODE_MANAGED_KEY_CATEGORY = "claude-code-managed";
/**
 * Freshly minted Claude Code key awaiting gateway verification (two-phase
 * rotation): the previous managed key stays active until the candidate is
 * verified and promoted, so a failed run never breaks a working setup. Stale
 * candidates are swept by the next begin/finalize.
 */
export const CLAUDE_CODE_MANAGED_CANDIDATE_CATEGORY = "claude-code-managed-candidate";
/** Display name of the key the Connect Claude Code flow mints; the raw key is never shown. */
export const CLAUDE_CODE_MANAGED_KEY_NAME = "CLAUDE-CODE-MANAGED-KEY";

export const CLI_MANAGED_KEY_CATEGORY = "cli-managed";
/**
 * Freshly minted `aireserve` CLI key awaiting gateway verification — same
 * two-phase rotation contract as the Claude Code candidate above, with one twist:
 * promotion supersedes CANDIDATES only. Promoted cli-managed keys are never
 * bulk-revoked by a later run — one key per machine (multi-machine installs),
 * and each key serves both Claude Code and Codex on that machine.
 */
export const CLI_MANAGED_CANDIDATE_CATEGORY = "cli-managed-candidate";
/** Display name of the key the /cli/setup handoff mints; the raw key is never shown. */
export const CLI_MANAGED_KEY_NAME = "AIRESERVE-CLI-KEY";

/** Org keys minted by the Connect GitHub Copilot admin flow. */
export const COPILOT_KEY_CATEGORY = "copilot";

/** Personal keys minted by the Connect Cursor guided flow. */
export const CURSOR_KEY_CATEGORY = "cursor";

/** Personal keys minted by the Connect Zed guided flow. */
export const ZED_KEY_CATEGORY = "zed";

/** Personal keys minted by the Connect Cline guided flow. */
export const CLINE_KEY_CATEGORY = "cline";

/** Personal keys minted by the Connect Factory Droid guided flow. */
export const DROID_KEY_CATEGORY = "droid";

/**
 * Keys used by CI/automation pipelines (AIR-871): set via the create-dialog
 * checkbox, the key-details retag toggle, or the Connect GitHub Actions flow.
 * Usage stamps harness "ci" and classifies deterministically as engineering.
 */
export const CI_KEY_CATEGORY = "ci";

/**
 * RunPod credentials (AIR-1554): org-level keys minted from Compute › RunPod
 * and bound to a RunPod permission (client_runpod_key_binding). The gateway
 * serves them on the RunPod passthrough only, swapped for the organization's
 * assigned RunPod Organization key. The value lives in @packages/llm-gateway
 * (a drizzle-free leaf) so this "use client"-safe registry can re-export it.
 */
export { ECS_KEY_CATEGORY, RUNPOD_KEY_CATEGORY };

// Org-scoped keys auto-minted by the @aireserve Slack mention agent (AIR-962).
// The value lives in @packages/llm-gateway (AIR-1487) because the /v1
// attribution guard that branches on it now also runs in apps/api-gateway;
// re-exported here so the registry below stays the single web-side home.
export { SLACK_AGENT_KEY_CATEGORY };
/** Display name of the key the mention agent mints; the raw key lives in client.settings.slackConnect.agentKey. */
export const SLACK_AGENT_KEY_NAME = "Slack agent (auto-minted, AIR-962)";


export interface ManagedKeyCategory {
  /** Badge text in the non-admin key table (admin view shows the raw category). */
  badge: string;
  /** Appended to the revoke confirmation when set. */
  revokeWarning?: string;
  /** Lifecycle is machine-owned: admins must not hand-assign this category. */
  reserved: boolean;
}

export const MANAGED_KEY_CATEGORIES: Record<string, ManagedKeyCategory> = {
  // Legacy (AIR-1390): the browser wizard that minted these is gone, so the
  // revoke warning points at the CLI, the only way to reconnect Codex now.
  [CODEX_MANAGED_KEY_CATEGORY]: {
    badge: "Managed · Codex (legacy)",
    revokeWarning:
      "Codex on your machine will stop working until you reconnect with the AI Reserve CLI (`aireserve configure`).",
    reserved: true,
  },
  [CODEX_MANAGED_CANDIDATE_CATEGORY]: {
    badge: "Managed · Codex (pending, legacy)",
    reserved: true,
  },
  [CLAUDE_CODE_MANAGED_KEY_CATEGORY]: {
    badge: "Managed · Claude Code",
    revokeWarning:
      "Claude Code on your machine will stop working until you reconnect from the Connect Claude Code page.",
    reserved: true,
  },
  [CLAUDE_CODE_MANAGED_CANDIDATE_CATEGORY]: {
    badge: "Managed · Claude Code (pending)",
    reserved: true,
  },
  [CLI_MANAGED_KEY_CATEGORY]: {
    badge: "Managed · CLI",
    revokeWarning:
      "Claude Code and Codex configured by the AI Reserve CLI on your machine(s) will stop working until you run `aireserve configure` again.",
    reserved: true,
  },
  [CLI_MANAGED_CANDIDATE_CATEGORY]: {
    badge: "Managed · CLI (pending)",
    reserved: true,
  },
  [COPILOT_KEY_CATEGORY]: {
    badge: "Copilot",
    reserved: false,
  },
  [CURSOR_KEY_CATEGORY]: {
    badge: "Cursor",
    revokeWarning:
      "Cursor on your machine will stop working until you paste a new key from the Connect Cursor page.",
    reserved: false,
  },
  [ZED_KEY_CATEGORY]: {
    badge: "Zed",
    revokeWarning:
      "Zed on your machine will stop working until you paste a new key from the Connect Zed page.",
    reserved: false,
  },
  [CLINE_KEY_CATEGORY]: {
    badge: "Cline",
    revokeWarning:
      "Cline in your editor will stop working until you paste a new key from the Connect Cline page.",
    reserved: false,
  },
  [DROID_KEY_CATEGORY]: {
    badge: "Factory Droid",
    revokeWarning:
      "Droid's AI Reserve models will stop working until you paste a new key from the Connect Factory Droid page.",
    reserved: false,
  },
  [CI_KEY_CATEGORY]: {
    badge: "CI",
    revokeWarning:
      "Pipelines using this key (e.g. GitHub Actions secrets) will fail until you rotate in a new key.",
    reserved: false,
  },
  [ECS_KEY_CATEGORY]: {
    badge: "Amazon ECS",
    revokeWarning:
      "AWS SDKs and CLIs using this credential as their access key id will stop reaching your container account until you create a new one from the AWS Hub.",
    reserved: true,
  },
  [RUNPOD_KEY_CATEGORY]: {
    badge: "RunPod",
    revokeWarning:
      "RunPod clients using this credential (SDK, runpodctl, MCP server) will stop working until you create a new one from Compute › RunPod.",
    reserved: true,
  },
  [SLACK_AGENT_KEY_CATEGORY]: {
    badge: "Slack agent",
    revokeWarning:
      "The @aireserve Slack agent will stop answering this org's mentions until AI Reserve re-issues its key.",
    reserved: true,
  },
};

export function isReservedManagedCategory(category: string): boolean {
  return MANAGED_KEY_CATEGORIES[category]?.reserved === true;
}

/**
 * The categories a person may put on a key they mint themselves: the
 * connect-flow tool tags (Copilot, Cursor, Zed, Cline, Droid) and CI. The
 * reserved ones above are machine-owned and never hand-assigned.
 */
export const SELF_SERVICE_KEY_CATEGORIES: readonly string[] = Object.entries(MANAGED_KEY_CATEGORIES)
  .filter(([, definition]) => !definition.reserved)
  .map(([category]) => category);
